/**
 * modelPrecedence – who wins when a student and the teacher both move a model.
 *
 * Students may shrink, expand, rotate and explode their own copy of a model.
 * The teacher's model tools still have to reach everyone. Before this, the
 * student-apply effect in the class player re-applied the teacher's whole model
 * state on every publish — including a publish that only changed the image on
 * screen — so a student's explode snapped back the moment the teacher showed a
 * picture. Any local gesture would have been wiped the same way.
 *
 * The rule that fixes it is "apply only what the teacher actually did":
 *
 *   - A teacher field is applied when it CHANGED since the student last applied
 *     it. Every merged publish stamps `touched_at.<field> = sync_id` for the
 *     fields it wrote, so a newer stamp means the teacher touched that field —
 *     even when the value is the same as before. That matters: a teacher who
 *     drags Explode back to where it already was still means "everyone, look at
 *     it this way", and must still override a student who wandered off.
 *   - A value that differs also counts as changed, stamps or not. States written
 *     before stamps existed carry none, and krpano's model publish still spreads
 *     the previous state into a whole-object write
 *     (VRLessonPlayerKrpano.tsx:2449-2458), which carries the OLD stamps forward
 *     next to a NEW value. Trusting the stamps alone would miss that change.
 *   - A teacher `exploded` overrides only the assets it targets: the selected
 *     asset, or every asset when none is selected. A student's explode on any
 *     other asset stays.
 *   - A teacher Reset (`reset_at`) clears every local override on every asset.
 *   - Isolate, part (id and name), section and highlight are teacher-only:
 *     students have no local version of them, so they never clear anything here.
 *   - Scale and yaw are always local. The teacher has no scale tool and the
 *     player never syncs `model_transform`, so only a Reset puts them back.
 *   - An image-only or sync-only publish changes no model field, so it causes
 *     no model operation at all.
 *
 * Pure: states and overrides go in, a set of fields and new overrides come out.
 * Every rule is tested in tests/modelPrecedence.test.mts; the player supplies
 * the snapshots and does the drawing.
 *
 * The teacher state is declared here structurally, rather than imported from
 * types/lms.ts, so this module and its test load without anything from the
 * Firebase side of the app. `TeacherContentState` is assignable to it.
 */

/** Explode amount, scale multiplier and yaw a student set on one asset, by asset key. */
export interface LocalModelOverrides {
  /** Explode amount 0..1, replacing the teacher's for that asset until the teacher touches it. */
  explode: Record<string, number>;
  /** Multiplier on the asset's dock (fit) scale. Absent means 1. */
  scale: Record<string, number>;
  /** Radians added to the asset's dock yaw. Absent means 0. */
  yaw: Record<string, number>;
}

/**
 * The part of `teacher_content_state` that decides what a student's models and
 * image panel show. A structural subset of `TeacherContentState` (types/lms.ts).
 */
export interface TeacherStateView {
  scene_key?: string | null;
  sync_id?: number;
  /** `sync_id` of the publish that last wrote each field. Absent on older writes. */
  touched_at?: Readonly<Record<string, number>> | null;
  /** `sync_id` of the teacher's last Reset. */
  reset_at?: number | null;
  /** 0..1, or a boolean from an older client (`true` reads as 1). */
  exploded?: number | boolean;
  selected_asset_key?: string | null;
  isolated?: boolean;
  selected_part_id?: string | null;
  /** Display label for the selected part; the player only mirrors it into its UI. */
  selected_part_name?: string | null;
  clip?: { axis: 'x' | 'y' | 'z'; offset: number } | null;
  highlighted_parts?: ReadonlyArray<{ part_id: string; label?: string | null; color?: string | null }> | null;
  active_image_id?: string | null;
}

/**
 * Every field whose change the player acts on, in the order it should apply
 * them: the target asset first, because explode and section act on the selected
 * asset and applying them before the selection would work on the wrong model.
 *
 * `selected_part_name` moves no model; it is here because the teacher can
 * publish it on its own (V3's model-mark tap sends a name-only patch). Left out,
 * that publish would come back as "nothing changed", and a player that mirrors
 * only changed fields into its UI would keep showing the old part label.
 */
export const TEACHER_FIELDS = [
  'selected_asset_key',
  'exploded',
  'isolated',
  'selected_part_id',
  'selected_part_name',
  'clip',
  'highlighted_parts',
  'reset_at',
  'active_image_id',
] as const;

export type TeacherField = (typeof TEACHER_FIELDS)[number];

export function emptyLocalOverrides(): LocalModelOverrides {
  return { explode: {}, scale: {}, yaw: {} };
}

/**
 * Which teacher fields changed between the state last applied and the one just
 * received.
 *
 * With no previous state, or one from a different scene, every field present in
 * `next` counts as changed: a late joiner, or a student on the first publish of
 * a new topic, has to catch up on everything. Absent fields are left out; the
 * models already sit in their default state for those.
 *
 * Re-collect caveat. When the model tools re-collect (an asset finished loading
 * late), the freshly collected models need every teacher value again, so the
 * player re-applies with `changedTeacherFields(null, next)`. It must NOT pass
 * that set to `clearForTeacher`, or every late-loading asset would wipe the
 * student's overrides (a present `reset_at` alone would clear them all). Clear
 * against the snapshot it last applied instead — normally nothing changed — and
 * then re-apply the surviving overrides on top.
 */
export function changedTeacherFields(
  prevApplied: TeacherStateView | null,
  next: TeacherStateView
): Set<TeacherField> {
  const changed = new Set<TeacherField>();
  const firstApply = prevApplied === null || (prevApplied.scene_key ?? null) !== (next.scene_key ?? null);

  for (const field of TEACHER_FIELDS) {
    if (firstApply) {
      if (next[field] !== undefined) changed.add(field);
      continue;
    }
    const nextStamp = stampOf(next, field);
    const prevStamp = stampOf(prevApplied, field);
    const touched = nextStamp !== undefined && (prevStamp === undefined || nextStamp > prevStamp);
    if (touched || !sameValue(normalise(field, prevApplied[field]), normalise(field, next[field]))) {
      changed.add(field);
    }
  }
  return changed;
}

/**
 * The student's overrides after the teacher's changes take precedence.
 *
 * `teacherTargetKeys` is the set the teacher's `exploded` acts on, read from the
 * NEW state: `[selected_asset_key]` when an asset is selected, otherwise every
 * asset key.
 *
 * Returns `overrides` itself when nothing is cleared, so the caller can skip
 * re-applying; otherwise a new object. The input is never mutated.
 */
export function clearForTeacher(
  overrides: LocalModelOverrides,
  changed: ReadonlySet<TeacherField>,
  teacherTargetKeys: readonly string[]
): LocalModelOverrides {
  if (changed.has('reset_at')) return emptyLocalOverrides();
  if (!changed.has('exploded')) return overrides;
  if (!teacherTargetKeys.some((key) => key in overrides.explode)) return overrides;

  const targets = new Set(teacherTargetKeys);
  const explode: Record<string, number> = {};
  for (const [key, amount] of Object.entries(overrides.explode)) {
    if (!targets.has(key)) explode[key] = amount;
  }
  return { explode, scale: { ...overrides.scale }, yaw: { ...overrides.yaw } };
}

/**
 * The image the class should be looking at in this scene, or null.
 *
 * `active_image_id` rides on the teacher state, which outlives a topic change
 * until the host's first publish in the new topic replaces it. Without the scene
 * check a student would open the next topic onto the previous topic's image.
 * An unknown scene on either side shows nothing rather than guessing.
 */
export function scopedActiveImageId(
  state: TeacherStateView | null | undefined,
  sceneKey: string | null | undefined
): string | null {
  if (!state || !sceneKey || state.scene_key !== sceneKey) return null;
  return state.active_image_id ?? null;
}

function stampOf(state: TeacherStateView, field: TeacherField): number | undefined {
  const stamp = state.touched_at?.[field];
  return typeof stamp === 'number' && Number.isFinite(stamp) ? stamp : undefined;
}

/**
 * Read a field the way the player applies it, so two spellings of the same
 * state compare equal: `exploded: true` is 1 and a missing one is 0, a missing
 * `isolated` is false.
 */
function normalise(field: TeacherField, value: unknown): unknown {
  if (field === 'exploded') return typeof value === 'number' ? value : value === true ? 1 : 0;
  if (field === 'isolated') return value === true;
  return value;
}

/**
 * Deep equality for Firestore-shaped values. Null and undefined both mean
 * "none": Firestore drops undefined, and a merged write keeps an explicit null,
 * so the same state can arrive either way.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return a == b;
  if (typeof a !== 'object' || typeof b !== 'object') return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => sameValue(item, b[i]));
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (!sameValue(left[key], right[key])) return false;
  }
  return true;
}
